import { IColumnConfig, TTableRow } from '../components/SmartTableGrid';
import { ISheetData } from '../hooks/useSheetData';
import {
  DEFAULT_EMPTY_ROW_COUNT,
  DEFAULT_EMPTY_COL_COUNT,
} from '../constants';
import { downloadFile } from '@home/utils/file';

/**
 * 转换列配置为下载所需格式
 * 优化：name 直接使用 title，为空则保持空白（不使用 col.data）
 * @param columns 表格列配置
 * @returns 下载所需的列配置
 */
export const convertColumnsToDownloadFormat = (columns: IColumnConfig[]) => columns.map(col => ({
  code: col.data,
  name: col.title || '', // 直接使用 title，为空则保持空白
  width: col.width || 150,
}));

/**
 * 生成空白行数据
 * @param columns 列配置
 * @param count 行数
 * @returns 空白行数据数组
 */
export const generateEmptyRows = (columns: IColumnConfig[], count: number): TTableRow[] => Array.from({ length: count }, () => {
  const row: TTableRow = {};
  columns.forEach(col => {
    row[col.data] = null;
  });
  return row;
});

/**
 * 生成空白列配置（无标题的空白列）
 * @param count 列数
 * @returns 空白列配置数组
 */
export const generateEmptyColumns = (count: number): IColumnConfig[] => Array.from({ length: count }, (_, index) => ({
  data: `col${index + 1}`,
  title: '', // 空标题，保持完全空白
  width: 150,
  sortable: true, // 新建列支持排序
  type: 'text' as const,
}));

/**
 * 生成完全空白的 sheet 数据（无预设列标题）
 * @returns 空白的 sheet 数据
 */
export const generateBlankSheetData = (): ISheetData => {
  const blankColumns = generateEmptyColumns(DEFAULT_EMPTY_COL_COUNT);
  return {
    tableData: generateEmptyRows(blankColumns, DEFAULT_EMPTY_ROW_COUNT),
    columns: blankColumns,
  };
};

/**
 * Pad sheet data to at least the default blank size (DEFAULT_EMPTY_ROW_COUNT rows × DEFAULT_EMPTY_COL_COUNT columns).
 * Only pads when actual data is smaller than the default; if larger, keeps as-is.
 * @param sheetData - The sheet data to pad
 * @returns Padded sheet data
 */
export const padSheetDataToMinSize = (sheetData: ISheetData): ISheetData => {
  const { tableData, columns } = sheetData;
  const currentRowCount = tableData.length;
  const currentColCount = columns.length;

  // Determine target sizes (at least default blank size)
  const targetRowCount = Math.max(currentRowCount, DEFAULT_EMPTY_ROW_COUNT);
  const targetColCount = Math.max(currentColCount, DEFAULT_EMPTY_COL_COUNT);

  // Pad columns if needed
  let paddedColumns = columns;
  if (currentColCount < targetColCount) {
    const extraColumns: IColumnConfig[] = Array.from(
      { length: targetColCount - currentColCount },
      (_, index) => ({
        data: `col${currentColCount + index + 1}`,
        title: '',
        width: 150,
        sortable: true,
        type: 'text' as const,
      }),
    );
    paddedColumns = [...columns, ...extraColumns];
  }

  // Pad existing rows with new column keys (if columns were added)
  let paddedData = tableData;
  if (currentColCount < targetColCount) {
    const newColKeys = paddedColumns.slice(currentColCount).map(col => col.data);
    paddedData = tableData.map(row => {
      const newRow = { ...row };
      newColKeys.forEach(key => {
        newRow[key] = null;
      });
      return newRow;
    });
  }

  // Pad rows if needed
  if (currentRowCount < targetRowCount) {
    const emptyRows = generateEmptyRows(paddedColumns, targetRowCount - currentRowCount);
    paddedData = [...paddedData, ...emptyRows];
  }

  return {
    tableData: paddedData,
    columns: paddedColumns,
  };
};

/** 检查值是否为空 */
const isEmpty = (v: any) => v === null || v === undefined || v === '';

/** 检查指定列是否全为空 */
const isColumnEmpty = (data: TTableRow[], colKey: string) => data.every(row => isEmpty(row[colKey]));

/** 检查指定行是否全为空 */
const isRowEmpty = (row: TTableRow, cols: IColumnConfig[]) => cols.every(col => isEmpty(row[col.data]));

/**
 * 裁剪 sheet 数据到有效数据范围，移除尾部空白行和空白列
 */
export const trimSheetDataToEffectiveRange = ({ tableData, columns }: ISheetData): ISheetData => {
  if (!tableData.length || !columns.length) return { tableData: [], columns: [] };

  // 找到最后一个非空列
  let lastCol = columns.length - 1;
  for (; lastCol >= 0 && isColumnEmpty(tableData, columns[lastCol].data);lastCol -= 1);
  if (lastCol < 0) return { tableData: [], columns: [] };

  const trimmedCols = columns.slice(0, lastCol + 1);

  // 找到最后一个非空行
  let lastRow = tableData.length - 1;
  for (; lastRow >= 0 && isRowEmpty(tableData[lastRow], trimmedCols);lastRow -= 1);
  if (lastRow < 0) return { tableData: [], columns: [] };

  return { tableData: tableData.slice(0, lastRow + 1), columns: trimmedCols };
};

/**
 * 将列索引转换为 Excel 列名（A, B, C, ..., Z, AA, AB, ...）
 * @param index 列索引（从 0 开始）
 * @returns Excel 列名
 */
export const getExcelColumnName = (index: number): string => {
  let name = '';
  let n = index;
  while (n >= 0) {
    name = String.fromCharCode((n % 26) + 65) + name;
    n = Math.floor(n / 26) - 1;
  }
  return name;
};

/**
 * CSV 下载配置接口
 */
export interface IDownloadCsvConfig {
  /** 文件名（不含扩展名） */
  fileName?: string;
  /** 数据源 */
  dataSource: Record<string, any>[];
  /** 列配置 */
  columns: { code: string; name: string; }[];
}

/**
 * 导出 CSV 文件
 * 支持中文（添加 BOM 头）
 * 优化：如果所有列名都为空，则使用第一行数据作为表头，数据从第二行开始
 * @param config CSV 下载配置
 */
export const downloadCsv = (config: IDownloadCsvConfig): void => {
  const { fileName = 'download', dataSource, columns } = config;

  if (!dataSource || dataSource.length === 0) {
    throw new Error('dataSource 不能为空');
  }

  const csvRows: string[] = [];

  // 检查是否有任何非空的列名
  const hasNonEmptyName = columns.some(col => col.name && col.name.trim() !== '');
  
  let actualDataSource = dataSource;
  
  if (hasNonEmptyName) {
    // 有列名，正常使用列名作为表头
    const headers = columns.map(col => col.name || '');
    csvRows.push(headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','));
  } else {
    // 所有列名为空，使用第一行数据作为表头，数据从第二行开始
    if (dataSource.length > 0) {
      const firstRow = dataSource[0];
      const headers = columns.map(col => {
        const value = firstRow?.[col.code] ?? '';
        return String(value);
      });
      csvRows.push(headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','));
      // 数据从第二行开始
      actualDataSource = dataSource.slice(1);
    }
  }

  // 添加数据行
  actualDataSource.forEach(record => {
    const rowData = columns.map(col => {
      const value = record?.[col.code] ?? '';
      const strValue = String(value);
      // 如果值包含逗号、换行符或双引号，需要用双引号包裹并转义
      if (strValue.includes(',') || strValue.includes('\n') || strValue.includes('"')) {
        return `"${strValue.replace(/"/g, '""')}"`;
      }
      return strValue;
    });
    csvRows.push(rowData.join(','));
  });

  // 添加 BOM 以支持中文
  const BOM = '\uFEFF';
  const csvContent = BOM + csvRows.join('\n');

  // 创建 Blob
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // 生成文件名
  const finalFileName = `${fileName}.csv`;

  // 使用公共方法下载
  downloadFile(blob, finalFileName);
};

/**
 * Excel 解析结果接口
 */
export interface IExcelParseResult {
  /** 表格数据 */
  tableData: TTableRow[];
  /** 列配置 */
  columns: IColumnConfig[];
  /** 工作表名称 */
  sheetName?: string;
  /** 是否被截取（数据量超过最大行数限制） */
  isTruncated?: boolean;
  /** 原始总行数（截取前） */
  originalRowCount?: number;
}

/** Excel 解析最大行数限制 */
export const MAX_EXCEL_ROWS = 10000;

/**
 * 将 Date 对象格式化为 yyyy-MM-dd 字符串
 * 兜底处理：当 xlsx 仍返回 Date 对象时使用
 */
export const formatDateValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

/**
 * 检测值的类型
 * @param value 要检测的值
 * @returns 'number' | 'date' | 'text'
 */
export const detectValueType = (value: any): 'number' | 'date' | 'text' => {
  if (value === null || value === undefined || value === '') {
    return 'text'; // 空值无法判断，默认为 text
  }
  
  // 检测是否为 Date 对象（xlsx 开启 cellDates 时会返回 Date 对象）
  if (value instanceof Date) {
    return 'date';
  }
  
  // 检测是否为数字
  if (typeof value === 'number' && !isNaN(value)) {
    return 'number';
  }
  
  // 字符串类型的进一步检测
  if (typeof value === 'string') {
    const trimmed = value.trim();
    
    // 检测是否为数字字符串
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return 'number';
    }
    
    // 检测是否为日期格式字符串（常见格式）
    // 支持: YYYY-MM-DD, YYYY/MM/DD, DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD HH:mm:ss 等
    const datePatterns = [
      /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/, // YYYY-MM-DD 或 YYYY/MM/DD
      /^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/, // DD-MM-YYYY 或 DD/MM/YYYY
      /^\d{4}[-/]\d{1,2}[-/]\d{1,2}\s+\d{1,2}:\d{2}(:\d{2})?$/, // 带时间的日期
      /^\d{4}年\d{1,2}月\d{1,2}日$/, // 中文日期格式
    ];
    
    for (const pattern of datePatterns) {
      if (pattern.test(trimmed)) {
        return 'date';
      }
    }
  }
  
  return 'text';
};

/**
 * 检测列的数据类型（基于数据行采样）
 * @param colKey 列的 data 字段名
 * @param dataRows 数据行数组
 * @param sampleSize 采样数量，默认 10
 * @returns 'number' | 'date' | 'text'
 */
export const detectColumnTypeByData = (
  colKey: string,
  dataRows: TTableRow[],
  sampleSize = 10
): 'number' | 'date' | 'text' => {
  const typeCounts: Record<string, number> = { number: 0, date: 0, text: 0 };
  const samplesToCheck = Math.min(sampleSize, dataRows.length);
  let validValueCount = 0;
  
  for (let i = 0;i < samplesToCheck;i++) {
    const value = dataRows[i]?.[colKey];
    // 跳过空值
    if (value === null || value === undefined || value === '') {
      continue;
    }
    // eslint-disable-next-line no-plusplus
    validValueCount++;
    const valueType = detectValueType(value);
    // eslint-disable-next-line no-plusplus
    typeCounts[valueType]++;
  }
  
  // 如果没有有效值，默认为 text
  if (validValueCount === 0) {
    return 'text';
  }
  
  // 如果超过 50% 的有效值是同一类型，则认为该列是该类型
  const threshold = validValueCount * 0.5;
  if (typeCounts.number >= threshold) {
    return 'number';
  }
  if (typeCounts.date >= threshold) {
    return 'date';
  }
  
  return 'text';
};

/**
 * 将检测到的数据类型映射为列配置的 type 属性
 * @param detectedType 检测到的类型 ('number' | 'date' | 'text')
 * @returns 列配置的 type 属性值
 */
const mapDetectedTypeToColumnType = (detectedType: 'number' | 'date' | 'text'): 'text' | 'numeric' | 'date' | 'percent' => {
  switch (detectedType) {
    case 'number':
      return 'numeric';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
};

/**
 * 根据表格数据动态更新列配置的 sortable 和 type 属性
 * 用于用户编辑后重新检测列类型
 * @param tableData 当前表格数据
 * @param columns 当前列配置
 * @param changedColKey 变化的列 data 字段名（可选，只检测指定列）
 * @returns 更新后的列配置
 */
export const updateColumnsWithTypeDetection = (
  tableData: TTableRow[],
  columns: IColumnConfig[],
  changedColKey?: string
): IColumnConfig[] => columns.map(col => {
  // 如果指定了变化的列，只检测该列
  if (changedColKey && col.data !== changedColKey) {
    return col;
  }
    
  // 空白列（无标题）也支持排序
  if (!col.title || col.title.trim() === '') {
    return { ...col, sortable: true, type: 'text' };
  }
    
  // 检测列类型
  const columnType = detectColumnTypeByData(col.data, tableData);
  // 只有数值类型和日期类型才启用排序
  const sortable = columnType === 'number' || columnType === 'date';
  // 将检测到的类型映射为列配置的 type 属性
  const type = mapDetectedTypeToColumnType(columnType);
    
  return { ...col, sortable, type };
});

/**
 * 解析 Excel ArrayBuffer 数据为 SmartTableGrid 格式
 * @param arrayBuffer Excel 文件的 ArrayBuffer 数据
 * @param sheetIndex 要解析的工作表索引，默认为 0（第一个工作表）
 * @param maxRows 最大行数限制，默认 10000，超出则截取
 * @returns 解析后的表格数据和列配置
 */
export const parseExcelArrayBuffer = async (
  arrayBuffer: ArrayBuffer,
  sheetIndex = 0,
  maxRows = MAX_EXCEL_ROWS
): Promise<IExcelParseResult> => {
  // 动态导入 xlsx 库
  const XLSX = await import('xlsx');
  
  // 读取 Excel 文件
  const workbook = XLSX.read(arrayBuffer, { 
    type: 'array',
    cellDates: false,   // 不将日期转为 Date 对象，保留原始数值
    cellNF: true,       // 保留单元格的数字格式字符串
    cellText: false,
  });
  
  // 获取工作表名称
  const sheetName = workbook.SheetNames[sheetIndex];
  if (!sheetName) {
    throw new Error(`工作表索引 ${sheetIndex} 不存在`);
  }
  
  // 获取工作表
  const worksheet = workbook.Sheets[sheetName];
  
  // 将工作表转换为 JSON 数据（包含表头）
  const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
    header: 1, // 返回数组格式，第一行为表头
    defval: null, // 空单元格默认值
    blankrows: true, // 包含空白行
    raw: false, // 使用格式化后的值，确保日期按原始格式展示
  });
  
  if (jsonData.length === 0) {
    return {
      tableData: [],
      columns: [],
      sheetName,
    };
  }
  
  // 第一行是表头
  const headerRow = jsonData[0] as (string | number | null)[];
  
  /**
   * 检测列的数据类型（基于数据行采样）
   * @param colIndex 列索引
   * @param dataRows 数据行（不含表头）
   * @param sampleSize 采样数量
   * @returns 'number' | 'date' | 'text'
   */
  const detectColumnType = (
    colIndex: number,
    dataRows: (string | number | null)[][],
    sampleSize = 10
  ): 'number' | 'date' | 'text' => {
    const typeCounts: Record<string, number> = { number: 0, date: 0, text: 0 };
    const samplesToCheck = Math.min(sampleSize, dataRows.length);
    let validValueCount = 0;
    
    for (let i = 0;i < samplesToCheck;i++) {
      const value = dataRows[i]?.[colIndex];
      // 跳过空值
      if (value === null || value === undefined || value === '') {
        continue;
      }
      // eslint-disable-next-line no-plusplus
      validValueCount++;
      const valueType = detectValueType(value);
      // eslint-disable-next-line no-plusplus
      typeCounts[valueType]++;
    }
    
    // 如果没有有效值，默认为 text
    if (validValueCount === 0) {
      return 'text';
    }
    
    // 如果超过 50% 的有效值是同一类型，则认为该列是该类型
    const threshold = validValueCount * 0.5;
    if (typeCounts.number >= threshold) {
      return 'number';
    }
    if (typeCounts.date >= threshold) {
      return 'date';
    }
    
    return 'text';
  };
  
  // 获取数据行（不含表头）用于类型检测
  const dataRows = jsonData.slice(1) as (string | number | null)[][];
  
  // 生成列配置，根据数据类型决定是否启用排序和设置列类型
  const columns: IColumnConfig[] = headerRow.map((header, index) => {
    const columnType = detectColumnType(index, dataRows);
    // 只有数值类型和日期类型才启用排序
    const sortable = columnType === 'number' || columnType === 'date';
    // 根据检测到的类型设置列的 type 属性（用于控制对齐方式）
    let type: string;
    if (columnType === 'number') {
      type = 'numeric';
    } else if (columnType === 'date') {
      type = 'date';
    } else {
      type = 'text';
    }
    
    return {
      data: `col${index + 1}`,
      title: header !== null && header !== undefined ? String(header) : '',
      width: 150,
      sortable,
      type: type as 'text' | 'numeric' | 'date' | 'percent',
    };
  });
  
  // 原始数据行数（不含表头）
  const originalRowCount = jsonData.length - 1;
  // 是否需要截取
  const isTruncated = originalRowCount > maxRows;
  // 实际处理的行数（截取后）
  const actualRowCount = isTruncated ? maxRows : originalRowCount;

  if (isTruncated) {
    console.warn(`Excel 数据行数 ${originalRowCount} 超过最大限制 ${maxRows}，已截取前 ${maxRows} 行`);
  }

  // 生成数据行（跳过表头，并应用行数限制）
  const tableData: TTableRow[] = [];
  for (let i = 1;i <= actualRowCount;i++) {
    const rowData = jsonData[i] as (string | number | null)[];
    const row: TTableRow = {};
    columns.forEach((col, colIndex) => {
      const value = rowData[colIndex];
      // 兜底处理：如果仍有 Date 对象，转为 yyyy-MM-dd HH:mm:ss 格式
      if (value instanceof Date) {
        row[col.data] = formatDateValue(value);
      } else {
        row[col.data] = value !== undefined ? value : null;
      }
    });
    tableData.push(row);
  }
  
  return {
    tableData,
    columns,
    sheetName,
    isTruncated,
    originalRowCount,
  };
};

/**
 * 解析 Excel ArrayBuffer 并获取所有工作表数据
 * @param arrayBuffer Excel 文件的 ArrayBuffer 数据
 * @returns 所有工作表的解析结果
 */
export const parseExcelAllSheets = async (
  arrayBuffer: ArrayBuffer
): Promise<IExcelParseResult[]> => {
  // 动态导入 xlsx 库
  const XLSX = await import('xlsx');
  
  // 读取 Excel 文件
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  
  const results: IExcelParseResult[] = [];
  
  for (let i = 0;i < workbook.SheetNames.length;i++) {
    const result = await parseExcelArrayBuffer(arrayBuffer, i);
    results.push(result);
  }
  
  return results;
};

/**
 * 将表格数据转换为 TSV 格式（Tab分隔）
 * 用于复制到剪贴板，兼容 Excel 等软件
 * @param data 表格数据二维数组
 * @returns TSV 格式字符串
 */
export const convertToTSV = (data: string[][]): string => data.map(row => row.join('\t')).join('\n');

/**
 * 将表格数据转换为 HTML 格式
 * 用于在 Word 中保持表格样式
 * @param data 表格数据二维数组
 * @returns HTML 格式的表格字符串
 */
export const convertToHTML = (data: string[][]): string => {
  if (!data || data.length === 0) return '';

  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // 计算每列的最小宽度（基于内容长度）
  const calculateColumnWidths = (): number[] => {
    if (data.length === 0) return [];
    
    const columnCount = data[0].length;
    const widths: number[] = new Array(columnCount).fill(100);
    
    data.forEach(row => {
      row.forEach((cell, colIndex) => {
        // 根据字符数估算宽度
        const chineseChars = (cell.match(/[\u4e00-\u9fa5]/g) || []).length;
        const otherChars = cell.length - chineseChars;
        const estimatedWidth = chineseChars * 14 + otherChars * 8 + 20;
        
        widths[colIndex] = Math.max(widths[colIndex], Math.min(estimatedWidth, 300));
      });
    });
    
    return widths;
  };

  const columnWidths = calculateColumnWidths();

  let html = '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; border: 1px solid #ddd; table-layout: auto;">';

  // 添加表头（第一行）
  if (data.length > 0) {
    html += '<thead><tr>';
    data[0].forEach((cell, index) => {
      const width = columnWidths[index] || 120;
      html += `<th style="background-color: #f5f5f5; border: 1px solid #ddd; padding: 10px 12px; text-align: left; font-weight: bold; min-width: ${width}px; white-space: nowrap;">${escapeHtml(cell)}</th>`;
    });
    html += '</tr></thead>';
  }

  // 添加表体（剩余行）
  if (data.length > 1) {
    html += '<tbody>';
    for (let i = 1;i < data.length;i++) {
      html += '<tr>';
      // eslint-disable-next-line no-loop-func
      data[i].forEach((cell, index) => {
        const width = columnWidths[index] || 120;
        html += `<td style="border: 1px solid #ddd; padding: 10px 12px; min-width: ${width}px;">${escapeHtml(cell)}</td>`;
      });
      html += '</tr>';
    }
    html += '</tbody>';
  }

  html += '</table>';
  return html;
};

/** 多 Sheet 数据结构（用于转换 Excel） */
export interface IMultiSheetData {
  /** 工作表名称 */
  sheetName: string;
  /** 表格数据 */
  tableData: TTableRow[];
  /** 列配置 */
  columns: IColumnConfig[];
}

/**
 * 将裁剪后的表格数据转换为二维数组（用于 XLSX aoa_to_sheet）
 * 始终以第一行数据作为表头，数据从第二行开始
 * @param trimmedData 裁剪后的表格数据
 * @param trimmedColumns 裁剪后的列配置
 * @returns 二维数组格式的工作表数据
 */
const buildWorksheetData = (trimmedData: TTableRow[], trimmedColumns: IColumnConfig[]): any[][] => {
  if (trimmedData.length === 0) return [];
  const firstRow = trimmedData[0];
  const headers = trimmedColumns.map((col) => {
    const value = firstRow[col.data];
    return value !== null && value !== undefined ? value : '';
  });
  const remainingData = trimmedData.slice(1);
  const rows = remainingData.map((row) => trimmedColumns.map((col) => {
    const value = row[col.data];
    return value !== null && value !== undefined ? value : '';
  }));
  return [headers, ...rows];
};

/**
 * 将表格数据转换为 Excel 文件 Blob（单个 sheet）
 * 优化：
 * 1. 如果所有列标题都为空，则使用第一行数据作为表头，数据从第二行开始
 * 2. 自动裁剪尾部空白行和空白列，确保导出文件紧凑整洁
 * @param data 表格数据数组
 * @param columns 列配置
 * @param sheetName 工作表名称
 * @returns Excel 文件的 Blob
 */
export const tableDataToExcelBlob = async (
  data: TTableRow[],
  columns: IColumnConfig[],
  sheetName = 'Sheet1'
): Promise<Blob> => {
  // 动态导入 xlsx 库
  const XLSX = await import('xlsx');

  // 裁剪有效数据范围，移除尾部空白行和空白列
  const { tableData: trimmedData, columns: trimmedColumns } = trimSheetDataToEffectiveRange({
    tableData: data,
    columns,
  });

  const worksheetData = buildWorksheetData(trimmedData, trimmedColumns);

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 生成 Excel 文件的二进制数据
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  // 转换为 Blob
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  return blob;
};

/**
 * 将多个 Sheet 数据转换为 Excel 文件 Blob（支持多 sheet）
 * 优化：
 * 1. 始终以第一行数据作为表头，数据从第二行开始
 * 2. 自动裁剪每个 sheet 尾部的空白行和空白列，确保导出文件紧凑整洁
 * @param sheetsData 多个 sheet 的数据数组
 * @returns Excel 文件的 Blob
 */
export const multiSheetDataToExcelBlob = async (
  sheetsData: IMultiSheetData[]
): Promise<Blob> => {
  // 动态导入 xlsx 库
  const XLSX = await import('xlsx');

  // 创建工作簿
  const workbook = XLSX.utils.book_new();

  // 遍历每个 sheet，添加到工作簿中
  sheetsData.forEach(({ sheetName, tableData, columns }) => {
    // 裁剪有效数据范围，移除尾部空白行和空白列
    const { tableData: trimmedData, columns: trimmedColumns } = trimSheetDataToEffectiveRange({
      tableData,
      columns,
    });

    const worksheetData = buildWorksheetData(trimmedData, trimmedColumns);

    // 创建工作表
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // 添加到工作簿（sheetName 不能超过 31 个字符，xlsx 规范限制）
    const safeSheetName = sheetName.slice(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
  });

  // 生成 Excel 文件的二进制数据
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  // 转换为 Blob
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  return blob;
};

/**
 * 将表格数据转换为 Excel File 对象（单个 sheet）
 * @param data 表格数据数组
 * @param columns 列配置
 * @param fileName 文件名（不含扩展名）
 * @param sheetName 工作表名称
 * @returns Excel 文件的 File 对象
 */
export const tableDataToExcelFile = async (
  data: TTableRow[],
  columns: IColumnConfig[],
  fileName: string,
  sheetName = 'Sheet1'
): Promise<File> => {
  const blob = await tableDataToExcelBlob(data, columns, sheetName);
  // 确保文件名以 .xlsx 结尾
  const finalFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  return new File([blob], finalFileName, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * 将多个 Sheet 数据转换为 Excel File 对象（支持多 sheet）
 * @param sheetsData 多个 sheet 的数据数组
 * @param fileName 文件名
 * @returns Excel 文件的 File 对象
 */
export const multiSheetDataToExcelFile = async (
  sheetsData: IMultiSheetData[],
  fileName: string
): Promise<File> => {
  const blob = await multiSheetDataToExcelBlob(sheetsData);
  // 确保文件名以 .xlsx 结尾
  const finalFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  return new File([blob], finalFileName, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * 复制表格到剪贴板（支持 Excel 和 Word 文档）
 * 参照 TableComponents 组件的实现
 * 
 * 优化点：
 * 1. 同时复制 HTML 和纯文本格式，兼容 Excel 和 Word
 * 2. HTML 格式在 Word 中保持表格样式和格式
 * 3. TSV 格式（Tab分隔）在 Excel 中自动识别为表格
 * @param tableData 表格数据二维数组（第一行为表头）
 * @returns 是否复制成功
 */
export const copyTableToClipboard = async (tableData: string[][]): Promise<boolean> => {
  try {
    if (!tableData || tableData.length === 0) {
      console.warn('表格数据为空');
      return false;
    }

    // 生成 HTML 和 TSV 格式
    const htmlData = convertToHTML(tableData);
    const tsvData = convertToTSV(tableData);

    // 检查是否支持 ClipboardItem
    const supportsClipboardItem = typeof ClipboardItem !== 'undefined';

    // 方案1: 使用 Clipboard API 同时复制多种格式（最佳方案）
    if (navigator.clipboard && supportsClipboardItem) {
      try {
        const htmlBlob = new Blob([htmlData], { type: 'text/html' });
        const textBlob = new Blob([tsvData], { type: 'text/plain' });
        
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        });

        await navigator.clipboard.write([clipboardItem]);
        return true;
      } catch (clipboardError) {
        console.warn('Clipboard API 失败，尝试降级方案:', clipboardError);
      }
    }

    // 方案2: 使用传统的 document.execCommand 方法（降级方案）
    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-9999px';
      tempDiv.innerHTML = htmlData;
      document.body.appendChild(tempDiv);

      const range = document.createRange();
      range.selectNodeContents(tempDiv);
      
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        
        const success = document.execCommand('copy');
        selection.removeAllRanges();
        document.body.removeChild(tempDiv);
        
        if (success) {
          return true;
        }
      }
      
      document.body.removeChild(tempDiv);
    } catch (execCommandError) {
      console.warn('execCommand 方法失败，使用最终降级方案:', execCommandError);
    }

    // 方案3: 最终降级方案 - 只复制纯文本
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(tsvData);
      return true;
    }

    return false;
  } catch (error) {
    console.error('复制表格失败:', error);
    return false;
  }
};
